import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import api, { reportsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import MultiSelectFilter from '@/components/MultiSelectFilter';
import CustomerDrillDownDialog from '@/components/CustomerDrillDownDialog';
import { toast } from 'sonner';
import {
  Percent, DownloadSimple, ArrowsClockwise, Users, Stethoscope, Buildings, Package, Receipt,
  CaretLeft, CaretRight, X,
} from '@phosphor-icons/react';

const fmt = (v) => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-';
const fmtD = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-';
const pct = (v) => `${Number(v || 0).toFixed(1)}%`;
const pctColor = (v) => v >= 50 ? 'text-red-600' : v >= 30 ? 'text-orange-600' : 'text-emerald-700';

const TABS = [
  { key: 'lines', label: 'Line Items', icon: Receipt },
  { key: 'by_customer', label: 'By Customer', icon: Users },
  { key: 'by_audiologist', label: 'By Audiologist', icon: Stethoscope },
  { key: 'by_clinic', label: 'By Clinic', icon: Buildings },
  { key: 'by_device', label: 'By Device', icon: Package },
];

const PAGE_SIZE = 50;

export default function DiscountReportPage() {
  const now = new Date();
  const fyStart = now.getMonth() >= 3 ? `${now.getFullYear()}-04-01` : `${now.getFullYear() - 1}-04-01`;
  const fyEnd = now.getMonth() >= 3 ? `${now.getFullYear() + 1}-03-31` : `${now.getFullYear()}-03-31`;

  const [from, setFrom] = useState(fyStart);
  const [to, setTo] = useState(fyEnd);
  const [haOnly, setHaOnly] = useState(true);
  const [category, setCategory] = useState('');
  const [clinic, setClinic] = useState('');
  const [audiologist, setAudiologist] = useState('');
  const [customer, setCustomer] = useState('');
  const [device, setDevice] = useState('');
  const [minPct, setMinPct] = useState('');
  const [maxPct, setMaxPct] = useState('');
  const [minSale, setMinSale] = useState('');
  const [maxSale, setMaxSale] = useState('');

  const [options, setOptions] = useState({ audiologists: [], categories: [], clinics: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('lines');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    reportsAPI.discountFilterOptions().then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const p = { start_date: from, end_date: to, ha_only: haOnly };
    if (!haOnly && category) p.category = category;
    if (clinic) p.clinic = clinic;
    if (audiologist) p.audiologist = audiologist;
    if (customer) p.customer = customer;
    if (device) p.device = device;
    if (minPct !== '') p.min_discount_pct = minPct;
    if (maxPct !== '') p.max_discount_pct = maxPct;
    if (minSale !== '') p.min_sale = minSale;
    if (maxSale !== '') p.max_sale = maxSale;
    return p;
  }, [from, to, haOnly, category, clinic, audiologist, customer, device, minPct, maxPct, minSale, maxSale]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.discounts({ ...buildParams(), page, page_size: PAGE_SIZE, sort_by: sortBy, sort_dir: sortDir });
      setData(data);
    } catch { toast.error('Failed to load discount report'); }
    finally { setLoading(false); }
  }, [buildParams, page, sortBy, sortDir]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = () => { setPage(1); load(); };
  const clearFilters = () => {
    setCategory(''); setClinic(''); setAudiologist(''); setCustomer(''); setDevice('');
    setMinPct(''); setMaxPct(''); setMinSale(''); setMaxSale(''); setPage(1);
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get('/reports/discounts/export', { params: buildParams(), responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = `discount_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(key); setSortDir('desc'); }
    setPage(1);
  };

  const summary = data?.summary || {};
  const totalPages = Math.ceil((data?.total || 0) / PAGE_SIZE);
  const groupKey = { by_customer: 'customer', by_audiologist: 'audiologist', by_clinic: 'clinic', by_device: 'device' }[tab];
  const groupRows = data?.[tab] || [];

  return (
    <div className="p-6" data-testid="discount-report-page">
      <PageHeader title="Discount Report" subtitle="Track sale price, discount & final price per customer, coach, clinic and device" />

      {/* Filters */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mt-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">FROM</label>
            <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-8 text-xs w-36" data-testid="disc-from" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">TO</label>
            <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-8 text-xs w-36" data-testid="disc-to" />
          </div>
          <Button size="sm" variant="outline" onClick={() => { setFrom(fyStart); setTo(fyEnd); }} className="h-8 text-xs">Current FY</Button>
          <label className="flex items-center gap-2 cursor-pointer text-xs text-[#0F172A] ml-2" data-testid="disc-ha-toggle">
            <input type="checkbox" checked={haOnly} onChange={e => setHaOnly(e.target.checked)} className="accent-[#0F4C81]" />
            Hearing-aid devices only
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {!haOnly && (
            <div>
              <label className="text-[10px] text-[#64748B] block mb-1">CATEGORY</label>
              <MultiSelectFilter value={String(category || '').split(',').filter(Boolean)} onChange={vals => setCategory(vals.join(','))}
              options={(options.categories || []).map(o => ({ value: o, label: o }))} allLabel="All Categories" noun="selected" testid="disc-category" buttonClassName="h-8 text-xs w-40" />
            </div>
          )}
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">CLINIC</label>
            <MultiSelectFilter value={String(clinic || '').split(',').filter(Boolean)} onChange={vals => setClinic(vals.join(','))}
              options={(options.clinics || []).map(o => ({ value: o, label: o }))} allLabel="All Clinics" noun="selected" testid="disc-clinic" buttonClassName="h-8 text-xs w-44" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">AUDIOLOGIST (COACH)</label>
            <MultiSelectFilter value={String(audiologist || '').split(',').filter(Boolean)} onChange={vals => setAudiologist(vals.join(','))}
              options={(options.audiologists || []).map(o => ({ value: o, label: o }))} allLabel="All Audiologists" noun="selected" testid="disc-audiologist" buttonClassName="h-8 text-xs w-44" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">CUSTOMER</label>
            <Input value={customer} onChange={e => setCustomer(e.target.value)} placeholder="Search name" className="h-8 text-xs w-40" data-testid="disc-customer" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">DEVICE</label>
            <Input value={device} onChange={e => setDevice(e.target.value)} placeholder="Search device" className="h-8 text-xs w-40" data-testid="disc-device" />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">DISCOUNT % (MIN / MAX)</label>
            <div className="flex gap-2">
              <Input type="number" value={minPct} onChange={e => setMinPct(e.target.value)} placeholder="Min" className="h-8 text-xs w-20" data-testid="disc-min-pct" />
              <Input type="number" value={maxPct} onChange={e => setMaxPct(e.target.value)} placeholder="Max" className="h-8 text-xs w-20" data-testid="disc-max-pct" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">SALE PRICE (MIN / MAX)</label>
            <div className="flex gap-2">
              <Input type="number" value={minSale} onChange={e => setMinSale(e.target.value)} placeholder="Min" className="h-8 text-xs w-24" data-testid="disc-min-sale" />
              <Input type="number" value={maxSale} onChange={e => setMaxSale(e.target.value)} placeholder="Max" className="h-8 text-xs w-24" data-testid="disc-max-sale" />
            </div>
          </div>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="outline" onClick={clearFilters} className="h-8 text-xs"><X size={12} className="mr-1" /> Clear</Button>
            <Button size="sm" onClick={applyFilters} className="h-8 text-xs bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="disc-apply"><ArrowsClockwise size={12} className="mr-1" /> Apply</Button>
            <Button size="sm" variant="outline" onClick={exportCsv} className="h-8 text-xs" data-testid="disc-export"><DownloadSimple size={12} className="mr-1" /> Export CSV</Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
        {[
          { label: 'Total Sale Price', value: fmt(summary.total_sale_price), color: 'bg-[#EBF3FA] text-[#0F4C81]', testId: 'disc-sum-sale' },
          { label: 'Total Discount', value: fmt(summary.total_discount), color: 'bg-red-50 text-red-700', testId: 'disc-sum-discount' },
          { label: 'Avg Discount %', value: pct(summary.avg_discount_pct), color: 'bg-orange-50 text-orange-700', testId: 'disc-sum-pct' },
          { label: 'Final Price', value: fmt(summary.total_final_price), color: 'bg-[#0F4C81] text-white', testId: 'disc-sum-final' },
          { label: 'Line Items', value: (summary.line_count || 0).toLocaleString('en-IN'), color: 'bg-blue-50 text-blue-800', testId: 'disc-sum-count' },
        ].map((k, i) => (
          <div key={i} className={`rounded-xl p-4 ${k.color}`} data-testid={k.testId}>
            <p className="text-[10px] uppercase tracking-wide opacity-70">{k.label}</p>
            <p className="text-xl font-bold mt-1">{k.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E2E8F0] mt-6 mb-4 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${tab === t.key ? 'border-[#0F4C81] text-[#0F4C81]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}
            data-testid={`disc-tab-${t.key}`}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center"><div className="inline-block w-6 h-6 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></div>
      ) : tab === 'lines' ? (
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto">
          <table className="w-full text-xs" data-testid="disc-lines-table">
            <thead>
              <tr className="bg-[#0F4C81] text-white text-[10px] uppercase">
                <th className="text-left p-2.5 cursor-pointer" onClick={() => toggleSort('date')}>Date {sortBy === 'date' ? (sortDir === 'desc' ? '▼' : '▲') : ''}</th>
                <th className="text-left p-2.5">Invoice</th>
                <th className="text-left p-2.5 cursor-pointer" onClick={() => toggleSort('customer')}>Customer</th>
                <th className="text-left p-2.5 cursor-pointer" onClick={() => toggleSort('device')}>Device</th>
                <th className="text-left p-2.5">Clinic</th>
                <th className="text-left p-2.5">Audiologist</th>
                <th className="text-right p-2.5">Qty</th>
                <th className="text-right p-2.5 cursor-pointer" onClick={() => toggleSort('sale_price')}>Sale Price {sortBy === 'sale_price' ? (sortDir === 'desc' ? '▼' : '▲') : ''}</th>
                <th className="text-right p-2.5 cursor-pointer" onClick={() => toggleSort('discount')}>Discount {sortBy === 'discount' ? (sortDir === 'desc' ? '▼' : '▲') : ''}</th>
                <th className="text-right p-2.5 cursor-pointer" onClick={() => toggleSort('discount_pct')}>Disc %</th>
                <th className="text-right p-2.5 cursor-pointer" onClick={() => toggleSort('final_price')}>Final Price</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r, i) => (
                <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`disc-row-${i}`}>
                  <td className="p-2.5 whitespace-nowrap">{fmtD(r.date)}</td>
                  <td className="p-2.5 font-mono text-[#0F4C81]">{r.invoice_number}</td>
                  <td className="p-2.5">
                    <button onClick={() => setDrill({ id: r.customer_id, name: r.customer_name })} className="text-[#0F4C81] underline decoration-dotted underline-offset-2 hover:text-[#0F4C81] text-left" data-testid={`disc-customer-${i}`}>{r.customer_name}</button>
                  </td>
                  <td className="p-2.5 max-w-[200px] truncate" title={r.device}>{r.device}{r.category ? <span className="ml-1 text-[10px] text-[#64748B]">({r.category})</span> : null}</td>
                  <td className="p-2.5">{r.clinic}</td>
                  <td className="p-2.5">{r.audiologist}</td>
                  <td className="p-2.5 text-right">{r.qty}</td>
                  <td className="p-2.5 text-right font-medium">{fmt(r.sale_price)}</td>
                  <td className="p-2.5 text-right text-red-600 font-medium">{fmt(r.discount)}</td>
                  <td className={`p-2.5 text-right font-semibold ${pctColor(r.discount_pct)}`}>{pct(r.discount_pct)}</td>
                  <td className="p-2.5 text-right font-semibold text-emerald-700">{fmt(r.final_price)}</td>
                </tr>
              ))}
              {!data?.rows?.length && <tr><td colSpan={11} className="text-center py-8 text-[#64748B]">No matching sales found for the selected filters.</td></tr>}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E8F0] bg-[#F8FAFC]">
              <span className="text-xs text-[#64748B]">Page {page} of {totalPages} · {data.total.toLocaleString('en-IN')} lines</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><CaretLeft size={14} /></Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><CaretRight size={14} /></Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto">
          <table className="w-full text-xs" data-testid={`disc-${tab}-table`}>
            <thead>
              <tr className="bg-[#0F4C81] text-white text-[10px] uppercase">
                <th className="text-left p-2.5">{TABS.find(t => t.key === tab)?.label.replace('By ', '')}</th>
                <th className="text-right p-2.5">Qty</th>
                <th className="text-right p-2.5">Lines</th>
                <th className="text-right p-2.5">Sale Price</th>
                <th className="text-right p-2.5">Discount</th>
                <th className="text-right p-2.5">Avg Discount %</th>
                <th className="text-right p-2.5">Final Price</th>
              </tr>
            </thead>
            <tbody>
              {groupRows.map((g, i) => (
                <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`disc-group-${i}`}>
                  <td className="p-2.5 font-medium max-w-[280px] truncate" title={g[groupKey]}>
                    {tab === 'by_customer' ? (
                      <button onClick={() => setDrill({ name: g.customer })} className="text-[#0F4C81] underline decoration-dotted underline-offset-2 hover:text-[#0F4C81] text-left" data-testid={`disc-group-customer-${i}`}>{g.customer}</button>
                    ) : g[groupKey]}
                  </td>
                  <td className="p-2.5 text-right">{g.qty}</td>
                  <td className="p-2.5 text-right">{g.count}</td>
                  <td className="p-2.5 text-right font-medium">{fmt(g.sale_price)}</td>
                  <td className="p-2.5 text-right text-red-600 font-medium">{fmt(g.discount)}</td>
                  <td className={`p-2.5 text-right font-semibold ${pctColor(g.avg_discount_pct)}`}>{pct(g.avg_discount_pct)}</td>
                  <td className="p-2.5 text-right font-semibold text-emerald-700">{fmt(g.final_price)}</td>
                </tr>
              ))}
              {!groupRows.length && <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">No data for the selected filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <CustomerDrillDownDialog open={!!drill} onOpenChange={o => { if (!o) setDrill(null); }} customerId={drill?.id || ''} customerName={drill?.name || ''} />
    </div>
  );
}
